import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useParams } from 'react-router-dom';
import { useAPI } from '../contexts/APIContext';
import LoadingScreen from '../components/LoadingScreen';
import VideoPlayer from '../components/VideoPlayer';
import { getPersonalizedStoryDescription } from "../utils/storyDescription";
import { setShareMeta } from '../utils/shareMeta';

const ShareEpisode = () => {
  const { id } = useParams();
  // Share token from the link (?token=…) — required by the gated share endpoint.
  const shareToken = new URLSearchParams(window.location.search).get('token');
  const { apiClient } = useAPI();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [charactersData, setCharactersData] = useState([]);

  // Poll for story updates if processing
  const applyShareMeta = (data) => {
    const name = data.title || data.name || 'A Pixley story';
    setShareMeta({
      title: `Watch “${name}” — made with Pixley`,
      description: data.description
        ? `${data.description} Made with Pixley — start your trial and create your own.`
        : 'An animated story made with Pixley. Start your trial and create your own.',
      image: data.thumbnail_url || data.cover_image_url,
    });
  };

  useEffect(() => {
    // Safari Smart App Banner: aim OPEN at this exact content. The base
    // app-id meta ships in index.html; here we add the per-page deep link.
    const meta = document.querySelector('meta[name="apple-itunes-app"]');
    if (meta) {
      meta.setAttribute(
        'content',
        `app-id=6751121342, app-argument=${window.location.href}`
      );
    }
  }, []);

  useEffect(() => {
    if (!story) return;
    
    const isProcessing = story.status === 'PROCESSING' || 
                        story.status === 'requested' || 
                        story.status === 'pending' ||
                        story.status === 'REQUESTED' ||
                        story.status === 'PENDING' ||
                        !story.file_url;

    if (!isProcessing) return;

    const pollInterval = setInterval(async () => {
      try {
        logger.log('🔄 Polling for shared story updates...');
        const response = await apiClient.getSharedEpisode(id, shareToken);
        if (response.status && response.data) {
          logger.log('📊 Polling response story:', response.data);
          setStory(response.data);
          applyShareMeta(response.data);
        }
      } catch (error) {
        logger.error('Error polling story updates:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [story, id, apiClient]);

  useEffect(() => {
    const loadSharedEpisode = async () => {
      if (!apiClient || !id) return;

      try {
        setLoading(true);
        setError(null);
        logger.log('🔗 Loading shared episode:', id);
        
        const response = await apiClient.getSharedEpisode(id, shareToken);
        logger.log('📊 Shared episode response:', response);

        if (response.status && response.data) {
          const storyData = response.data;
          setStory(storyData);
          applyShareMeta(storyData);

          // Shared responses embed character summaries (backend 1A) — use them
          // instead of the now-gated per-character /characters/{id} fetches.
          if (Array.isArray(storyData.character_details) && storyData.character_details.length > 0) {
            setCharactersData(storyData.character_details);
          } else if (storyData.characters && storyData.characters.length > 0) {
            logger.log('🎭 Story characters found:', storyData.characters);

            const charactersPromises = storyData.characters.map(async (characterId) => {
              try {
                if (typeof characterId === 'string') {
                  logger.log('🔍 Loading character details for ID:', characterId);
                  // Try to get character details (this may or may not work depending on backend implementation)
                  const charResponse = await apiClient.getCharacter(characterId);
                  if (charResponse.status) {
                    logger.log('✅ Character loaded successfully:', charResponse.data);
                    return charResponse.data;
                  } else {
                    logger.warn('⚠️ Failed to load character, response:', charResponse);
                    return null;
                  }
                }
                // If it's already an object
                logger.log('📋 Character is already an object:', characterId);
                return characterId;
              } catch (error) {
                logger.error('❌ Failed to load character:', characterId, error);
                return null;
              }
            });

            const loadedCharacters = await Promise.all(charactersPromises);
            const validCharacters = loadedCharacters.filter(char => char !== null);
            logger.log('🎭 Final loaded characters:', validCharacters);
            setCharactersData(validCharacters);
          }
        } else {
          setError('Episode not found or not available for sharing');
        }
      } catch (error) {
        logger.error('Failed to load shared episode:', error);
        setError('Failed to load episode');
      } finally {
        setLoading(false);
      }
    };

    loadSharedEpisode();
  }, [id, apiClient]);

  const handlePlayStory = () => {
    if (story.file_url) {
      setIsVideoPlayerOpen(true);
    }
  };

  const closeVideoPlayer = () => {
    setIsVideoPlayerOpen(false);
  };

  if (loading) {
    return <LoadingScreen message="Loading episode..." type="story" />;
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/50 to-pink-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
          <div className="text-center py-16">
            <div className="text-6xl mb-6">😞</div>
            <h1 className="text-3xl font-bold text-white mb-4">Episode Not Found</h1>
            <p className="text-gray-300 mb-8">
              {error || "Sorry, we couldn't find this episode."}
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all duration-200"
            >
              Go to Pixley
            </a>
          </div>
        </div>
      </div>
    );
  }

  const hasVideoFile = story.file_url && story.file_url.trim() !== '';
  const isProcessing = story.status === 'PROCESSING' || 
                      story.status === 'requested' || 
                      story.status === 'pending' ||
                      story.status === 'REQUESTED' ||
                      story.status === 'PENDING' ||
                      !hasVideoFile;
  
  return (
    <div className="story-detail-container min-h-screen">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-16 left-16 w-40 h-40 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute top-32 right-24 w-32 h-32 bg-gradient-to-r from-pink-500/20 to-red-500/20 rounded-full blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-40 left-1/3 w-24 h-24 bg-gradient-to-r from-green-500/20 to-teal-500/20 rounded-full blur-lg animate-pulse animation-delay-4000"></div>
        <div className="absolute bottom-24 right-1/4 w-36 h-36 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full blur-xl animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        {/* Story Content */}
        <div className="space-y-8">
          {/* Main Video/Thumbnail Section */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-500">
            <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
              {hasVideoFile && !isProcessing ? (
                <>
                  <video
                    className="w-full h-full object-contain"
                    muted
                    loop
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      const video = e.target;
                      if (video.duration && video.duration > 0) {
                        video.currentTime = video.duration / 2;
                      }
                    }}
                  >
                    <source src={story.file_url} type="video/mp4" />
                  </video>
                  
                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-all duration-300 cursor-pointer" onClick={handlePlayStory}>
                    <button className="w-20 h-20 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-200 shadow-lg">
                      <svg className="w-8 h-8 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                </>
              ) : isProcessing ? (
                <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-blue-900/50 flex flex-col items-center justify-center">
                  <div className="relative w-24 h-24 mb-4">
                    <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-white border-r-transparent rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{story.status_percentage || 0}%</span>
                    </div>
                  </div>
                  <p className="text-white text-lg font-medium">Processing episode...</p>
                  <p className="text-white/60 text-sm mt-2">This may take a few minutes</p>
                </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <img
                    src={story.thumbnail_url || story.cover_image_url || '/assets/story-placeholder.png'}
                    alt={story.title}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.src = '/assets/story-placeholder.png';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Story Info */}
            <div className="p-8 space-y-6">
              <div>
                <h1 className="text-4xl font-bold text-white mb-4">{story.title || story.name || 'Untitled Story'}</h1>
                {story.description && (
                  <p className="text-lg text-gray-300 leading-relaxed">{getPersonalizedStoryDescription(story)}</p>
                )}
              </div>

              {/* Metadata */}
              <div className="flex flex-wrap items-center gap-4">
                {story.theme && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                    <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span className="text-white font-medium">{story.theme}</span>
                  </div>
                )}

                {story.created_at && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                    <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-white font-medium">
                      {new Date(story.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}

                <div className={`flex items-center gap-2 px-4 py-2 backdrop-blur-sm rounded-full ${
                  isProcessing ? 'bg-yellow-500/20' : 'bg-green-500/20'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    isProcessing ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
                  }`}></div>
                  <span className="text-white font-medium">
                    {isProcessing ? 'Processing' : 'Ready'}
                  </span>
                </div>
              </div>

              {/* Characters */}
              {((charactersData && charactersData.length > 0) || (story.characters && story.characters.length > 0)) && (
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Characters</h3>
                  <div className="flex flex-wrap gap-4">
                    {charactersData && charactersData.length > 0 ? (
                      charactersData.map((character, index) => (
                        <div
                          key={character?.id || index}
                          className="flex items-center gap-3 px-4 py-3 bg-white/10 backdrop-blur-sm rounded-2xl hover:bg-white/20 transition-all duration-200 min-w-0"
                        >
                          {/* Character Avatar */}
                          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-purple-400 to-pink-400">
                            {character?.animated_image_url ? (
                              <img
                                src={character.animated_image_url}
                                alt={character?.name || 'Character'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="w-full h-full flex items-center justify-center" style={{display: character?.animated_image_url ? 'none' : 'flex'}}>
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          </div>

                          {/* Character Name */}
                          <span className="text-white font-medium truncate">
                            {character?.name || 'Unknown Character'}
                          </span>
                        </div>
                      ))
                    ) : (
                      story.characters.map((character, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 px-4 py-3 bg-white/10 backdrop-blur-sm rounded-2xl hover:bg-white/20 transition-all duration-200 min-w-0"
                        >
                          {/* Character Avatar - default icon */}
                          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>

                          {/* Character Name */}
                          <span className="text-white font-medium truncate">
                            {typeof character === 'string' ? character : (character?.name || 'Unknown Character')}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* CTA Section */}
              <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 p-8 shadow-2xl mt-8">
                <div className="text-center space-y-4">
                  <h3 className="text-2xl font-bold text-white">Create Your Own Stories!</h3>
                  <p className="text-gray-300 text-lg">
                    Join Pixley to make AI-powered animated stories with your own characters. Every membership starts with a trial.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <a
                      href={`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                      className="group px-8 py-4 bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 hover:from-purple-700 hover:via-purple-800 hover:to-pink-700 text-white rounded-2xl font-semibold transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/40 active:scale-95 transform hover:-translate-y-1 border border-white/10"
                    >
                      <div className="flex items-center justify-center space-x-3">
                        <span>Try Pixley — Make Your Own</span>
                        <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </a>
                    <a
                      href="/"
                      className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-2xl font-semibold transition-all duration-200 hover:shadow-lg border border-white/20"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="relative z-10 bg-gradient-to-br from-purple-900/80 to-pink-900/80 backdrop-blur-xl border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-300 text-lg mb-6">
              Want to create stories like this? Download Pixley on the App Store!
            </p>
            <a
              href="https://apps.apple.com/us/app/pixley-personalized-cartoons/id6751121342"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img 
                src="/assets/download_on_app_store.svg" 
                alt="Download on the App Store" 
                className="h-12 hover:scale-105 transition-transform duration-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </a>
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      <VideoPlayer
        isOpen={isVideoPlayerOpen && story.file_url}
        videoUrl={story.file_url}
        title={story.title || story.name || 'Story'}
        onClose={closeVideoPlayer}
        storyId={story?.id}
      />
    </div>
  );
};

export default ShareEpisode;